import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule, NgFor, NgIf } from "@angular/common";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import Swal from "sweetalert2";

import { GloControlComponent } from "../../../../shared/components/glo-control/glo-control.component";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { ApiService } from "../../../../shared/services/api/api.service";
import { environment } from "../../../../../environments/environment";

declare global {
  interface Window {
    Square?: any;
  }
}

interface BusinessInvoiceItem {
  id: string;
  appointment_id: string;
  salon_id: string;
  salon_name: string;
  appointment_created_at: string | null;
  appointment_start_time: string | null;
  appointment_status: string;
  amount: string;
  created_at: string;
}

interface BusinessInvoice {
  id: string;
  name: string;
  period_start: string;
  period_end: string;
  appointment_count: number;
  price_per_appointment: string;
  subtotal: string;
  tax_amount: string;
  amount: string;
  status: string;
  is_paid: boolean;
  plan: string;
  provider_invoice_id?: string | null;
  invoice_url?: string | null;
  issued_at?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  items?: BusinessInvoiceItem[];
}

@Component({
  selector: "business-account-billing",
  standalone: true,
  imports: [
    CommonModule,
    NgFor,
    NgIf,
    GloControlComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: "./billing.component.html",
  styleUrl: "./billing.component.scss",
})
export class BusinessAccountBillingComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  assetUrl = environment.assetBaseUrl;

  @ViewChild("squareCardContainer")
  squareCardContainer!: ElementRef<HTMLDivElement>;

  accountBillingDeatilForm!: FormGroup;

  isLoading = false;
  isSubmitting = false;

  squareError = "";

  billingMonths: BusinessInvoice[] = [];

  countries = ["Australia", "United States", "Canada", "United Kingdom"];

  hasExistingCard = false;
  showCardEntry = true;
  existingCardSummary = "";

  paymentAuthorized = false;
  authorizationAcceptedAt: string | null = null;

  agreementVersion = "";
  agreementText = "";
  acceptRecurringBilling = false;

  isRevokingAuthorization = false;

  showInvoiceModal = false;
  invoiceDetailLoading = false;

  selectedInvoice: BusinessInvoice | null = null;

  downloadingInvoiceId: string | null = null;

  private payments: any | null = null;
  private card: any | null = null;
  private squareInitialized = false;
  private destroyed = false;

  private squareAppId = (environment as any).squareAppId;

  private squareLocationId =
    (environment as any).squareLocationId || "SANDBOX_LOCATION_ID";

  private useSandbox = (environment as any).squareSandbox ?? true;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
  ) {
    this.accountBillingDeatilForm = this.fb.group({
      cardDetails: this.fb.group({
        name: ["", Validators.required],
      }),

      address: this.fb.group({
        address: ["", Validators.required],
        city: ["", Validators.required],
        state: ["", Validators.required],
        zip: ["", Validators.required],
        country: ["", Validators.required],
      }),

      invoices: this.fb.array([]),

      email: ["", [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.getBillingDetails();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.safeInitSquareCard();
  }

  ngOnDestroy(): void {
    this.destroyed = true;

    this.card = null;
    this.payments = null;

    document.body.style.overflow = "";
  }

  get invoicesFormArray(): FormArray {
    return this.accountBillingDeatilForm.get("invoices") as FormArray;
  }

  // ========================================================
  // BILLING
  // ========================================================

  getBillingDetails(): void {
    this.isLoading = true;

    this.apiService
      .request<any>(API_ENDPOINTS.owner.accountSettings.billing, "GET")
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          if (response?.data) {
            this.populateBillingForm(response.data);
          }
        },

        error: (error) => {
          this.isLoading = false;

          console.log("Billing load error", error);
        },
      });
  }

  populateBillingForm(data: any): void {
    const billing = data.billing || data;

    this.hasExistingCard = !!billing?.cardNumber || !!billing?.card_last4;

    this.existingCardSummary =
      billing?.cardNumber ||
      (billing?.card_last4 ? `•••• ${billing.card_last4}` : "");

    if (this.hasExistingCard) {
      this.showCardEntry = false;
    }

    const authorization = data.payment_authorization || {};

    this.paymentAuthorized = authorization.authorized === true;
    this.authorizationAcceptedAt = authorization.accepted_at || null;

    this.agreementVersion =
      authorization.current_agreement_version ||
      authorization.agreement_version ||
      "";

    this.agreementText =
      authorization.current_agreement_text ||
      authorization.agreement_text ||
      "";

    this.acceptRecurringBilling = false;

    this.accountBillingDeatilForm.patchValue({
      cardDetails: {
        name: billing.cardName || billing.cardholder_name || "",
      },

      address: {
        address: billing.street || billing.address || "",

        city: billing.city || "",

        state: billing.state || "",

        zip: billing.zip || billing.zip_code || "",

        country: billing.country || "",
      },

      email: billing.email || billing.billing_email || "",
    });

    this.setInvoices(data.invoices || []);
  }

  setInvoices(invoices: BusinessInvoice[]): void {
    this.billingMonths = invoices || [];

    this.invoicesFormArray.clear();

    this.billingMonths.forEach((invoice) => {
      this.invoicesFormArray.push(
        this.fb.group({
          id: [invoice.id],
          month: [invoice.name || ""],
          status: [invoice.status || ""],
          price: [invoice.amount || ""],
          plan: [invoice.plan || ""],
        }),
      );
    });
  }

  // ========================================================
  // INVOICE DETAIL
  // ========================================================

  viewInvoice(invoice: BusinessInvoice): void {
    if (!invoice?.id) {
      return;
    }

    this.selectedInvoice = {
      ...invoice,
      items: [],
    };

    this.showInvoiceModal = true;
    this.invoiceDetailLoading = true;

    document.body.style.overflow = "hidden";

    const endpoint = API_ENDPOINTS.owner.accountSettings.invoiceDetail(
      invoice.id,
    );

    this.apiService.request<any>(endpoint, "GET").subscribe({
      next: (response) => {
        this.invoiceDetailLoading = false;

        if (response?.data) {
          this.selectedInvoice = response.data;
        }
      },

      error: (error) => {
        this.invoiceDetailLoading = false;

        console.log("Invoice detail error", error);

        this.closeInvoiceModal();

        /*
         * ApiService already displays its global
         * HTTP error alert. Do not display a
         * second SweetAlert here.
         */
      },
    });
  }

  closeInvoiceModal(): void {
    this.showInvoiceModal = false;
    this.invoiceDetailLoading = false;
    this.selectedInvoice = null;

    document.body.style.overflow = "";
  }

  // ========================================================
  // PDF
  // ========================================================

  downloadInvoicePdf(invoice: BusinessInvoice): void {
    if (!invoice?.id || this.downloadingInvoiceId) {
      return;
    }

    this.downloadingInvoiceId = invoice.id;

    const endpoint = API_ENDPOINTS.owner.accountSettings.invoicePdf(invoice.id);

    this.apiService.download(endpoint).subscribe({
      next: (blob) => {
        this.downloadingInvoiceId = null;

        const fileName = this.buildInvoiceFileName(invoice);

        this.saveBlob(blob, fileName);
      },

      error: (error) => {
        this.downloadingInvoiceId = null;

        console.log("Invoice PDF download error", error);

        /*
         * ApiService.download() already displays
         * the global HTTP error alert.
         */
      },
    });
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = fileName;

    document.body.appendChild(anchor);

    anchor.click();
    anchor.remove();

    window.URL.revokeObjectURL(url);
  }

  private buildInvoiceFileName(invoice: BusinessInvoice): string {
    let period = "";

    if (invoice.period_start) {
      const date = new Date(invoice.period_start);

      if (!Number.isNaN(date.getTime())) {
        period =
          `${date.getFullYear()}-` +
          `${String(date.getMonth() + 1).padStart(2, "0")}`;
      }
    }

    return period
      ? `gloget-Invoice-${period}.pdf`
      : `gloget-Invoice-${invoice.id}.pdf`;
  }

  // ========================================================
  // SQUARE
  // ========================================================

  async onReplaceCard(): Promise<void> {
    this.showCardEntry = true;

    /*
     * Wait for Angular to render the Square
     * card container.
     */
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 0));

    /*
     * Reset the old Square card instance before
     * mounting the replacement card form.
     */
    this.card = null;
    this.payments = null;
    this.squareInitialized = false;

    await this.safeInitSquareCard();
  }

  async safeInitSquareCard(): Promise<void> {
    if (this.destroyed || this.squareInitialized || !this.showCardEntry) {
      return;
    }

    try {
      await this.ensureSquareSdkLoaded();

      await this.initSquareCard();

      this.squareInitialized = true;
      this.squareError = "";
    } catch (error: any) {
      this.squareError =
        error?.message || "Failed to initialize Square payment form.";
    }
  }

  private async ensureSquareSdkLoaded(): Promise<void> {
    if (window.Square) {
      return;
    }

    const src = this.useSandbox
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";

    await new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve());

        existingScript.addEventListener("error", () =>
          reject(new Error("Square SDK failed to load.")),
        );

        return;
      }

      const script = document.createElement("script");

      script.src = src;
      script.async = true;

      script.onload = () => resolve();

      script.onerror = () => reject(new Error("Square SDK failed to load."));

      document.head.appendChild(script);
    });
  }

  private async initSquareCard(): Promise<void> {
    if (!window.Square) {
      throw new Error("Square SDK not available.");
    }

    if (!this.squareAppId || !this.squareLocationId) {
      throw new Error("Missing Square appId/locationId configuration.");
    }

    this.payments = window.Square.payments(
      this.squareAppId,
      this.squareLocationId,
    );

    this.card = await this.payments.card();

    await this.card.attach("#square-card-container");
  }

  // ========================================================
  // SAVE
  // ========================================================

  async handleSave(): Promise<void> {
    if (this.isSubmitting || this.isLoading) {
      return;
    }

    this.squareError = "";

    if (this.accountBillingDeatilForm.invalid) {
      this.accountBillingDeatilForm.markAllAsTouched();
      return;
    }

    const savingNewCard = this.showCardEntry;

    if (savingNewCard && !this.acceptRecurringBilling) {
      this.squareError =
        "You must accept the recurring billing agreement before saving your card.";
      return;
    }

    if (savingNewCard && !this.agreementVersion) {
      this.squareError =
        "The billing agreement could not be loaded. Please refresh and try again.";
      return;
    }

    this.isSubmitting = true;

    try {
      const formValue = this.accountBillingDeatilForm.value;

      let squareToken: string | null = null;

      if (savingNewCard) {
        if (!this.card) {
          throw new Error("Square payment form is not ready yet.");
        }

        const tokenizeResult = await this.card.tokenize();

        if (tokenizeResult.status !== "OK" || !tokenizeResult.token) {
          throw new Error(
            tokenizeResult?.errors?.[0]?.message || "Card tokenization failed.",
          );
        }

        squareToken = tokenizeResult.token;
      }

      const payload = {
        token: squareToken,
        cardName: formValue.cardDetails.name,
        email: formValue.email,
        street: formValue.address.address,
        city: formValue.address.city,
        state: formValue.address.state,
        zip: formValue.address.zip,
        country: formValue.address.country,

        accept_recurring_billing: savingNewCard && this.acceptRecurringBilling,

        agreement_version: savingNewCard ? this.agreementVersion : null,
      };

      this.apiService
        .request<any>(
          API_ENDPOINTS.owner.accountSettings.billing,
          "POST",
          payload,
        )
        .subscribe({
          next: (response) => {
            this.isSubmitting = false;

            if (response?.success === false) {
              this.squareError =
                response.message || "Unable to save billing details.";
              return;
            }

            this.acceptRecurringBilling = false;

            Swal.fire({
              icon: "success",
              title: "Billing saved successfully",
              toast: true,
              position: "top-end",
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              customClass: {
                container: "!z-[999999]",
              },
            });

            this.getBillingDetails();
          },

          error: (error) => {
            this.isSubmitting = false;
            console.error("Billing save error", error);
          },
        });
    } catch (error: any) {
      this.isSubmitting = false;
      this.squareError = error?.message || "Failed to save billing details.";
    }
  }

  async revokeRecurringBilling(): Promise<void> {
    if (!this.paymentAuthorized || this.isRevokingAuthorization) {
      return;
    }

    const result = await Swal.fire({
      title: "Revoke automatic billing?",
      text:
        "gloget will no longer be authorized to automatically charge " +
        "your saved card. Outstanding invoices may still be payable.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revoke authorization",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#b91c1c",
    });

    if (!result.isConfirmed) {
      return;
    }

    this.isRevokingAuthorization = true;

    const endpoint = API_ENDPOINTS.owner.accountSettings.billing.replace(
      /\/?$/,
      "/authorization/",
    );

    this.apiService.request<any>(endpoint, "DELETE").subscribe({
      next: (response) => {
        this.isRevokingAuthorization = false;

        if (response?.success === false) {
          return;
        }

        this.paymentAuthorized = false;
        this.authorizationAcceptedAt = null;
        this.acceptRecurringBilling = false;

        Swal.fire({
          icon: "success",
          title: "Automatic billing authorization revoked",
          toast: true,
          position: "top-end",
          timer: 3000,
          showConfirmButton: false,
        });

        this.getBillingDetails();
      },

      error: (error) => {
        this.isRevokingAuthorization = false;
        console.error("Authorization revocation error", error);
      },
    });
  }

  // ========================================================
  // DISPLAY HELPERS
  // ========================================================

  formatMoney(value: string | number | null | undefined): string {
    const amount = Number(value ?? 0);

    if (Number.isNaN(amount)) {
      return "$0.00";
    }

    return `$${amount.toFixed(2)}`;
  }

  formatBillingPeriod(invoice: BusinessInvoice): string {
    if (!invoice.period_start || !invoice.period_end) {
      return "";
    }

    const start = new Date(invoice.period_start);

    /*
     * period_end is exclusive on the backend,
     * so show the previous calendar day.
     */
    const end = new Date(invoice.period_end);

    end.setDate(end.getDate() - 1);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "";
    }

    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };

    return (
      `${start.toLocaleDateString("en-CA", options)} – ` +
      `${end.toLocaleDateString("en-CA", options)}`
    );
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  formatStatus(value: string | null | undefined): string {
    if (!value) {
      return "—";
    }

    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  shortId(value: string | null | undefined): string {
    if (!value) {
      return "";
    }

    return `#${value.substring(0, 8)}`;
  }

  isPaid(invoice: BusinessInvoice): boolean {
    return invoice?.status?.toLowerCase() === "paid";
  }

  isOpen(invoice: BusinessInvoice): boolean {
    return invoice?.status?.toLowerCase() === "open";
  }

  isFailed(invoice: BusinessInvoice): boolean {
    return invoice?.status?.toLowerCase() === "failed";
  }

  isVoid(invoice: BusinessInvoice): boolean {
    return invoice?.status?.toLowerCase() === "void";
  }

  // ========================================================
  // GENERAL
  // ========================================================

  onCancel(): void {
    this.getBillingDetails();
  }

  isInvalid(path: string): boolean {
    const control = this.accountBillingDeatilForm.get(path);

    return !!control?.invalid && (control?.touched || control?.dirty);
  }
}
